/** At most three attempts in total, counting the first. One recording prepare writes a room web three times as it runs (ORB-1086). */
const STALE_ETAG_ATTEMPTS = 3

/**
 * Runs a write that carries a document's ETag. If the server answers 412 (Precondition Failed), the document
 * changed since it was read: read it again and retry against the fresh copy, up to {@link STALE_ETAG_ATTEMPTS} tries
 * in all. Meant for writes that state an intent ("sign this off", "add this poll"), which still hold on the newer
 * document; sending the person off to refresh and click again would only repeat the same request.
 *
 * The first retry rereads at once, which covers a single write elsewhere. A second 412 means writes are still landing,
 * as when two recording prepares finish close together, so each later retry first waits a short, jittered moment.
 *
 * `attempt` gets the document, not just its ETag, so a write built from the document's contents (a whole poll
 * list) is rebuilt from the fresh copy rather than overwriting what someone else just saved.
 */
export async function retryOnStaleETag<TDocument extends { ETag: string }, TResponse extends { status?: number }>(
    attempt: (document: TDocument) => Promise<TResponse>,
    current: TDocument,
    refetch: () => Promise<TDocument | null>,
): Promise<TResponse> {
    let response = await attempt(current)
    for (let tries = 1; tries < STALE_ETAG_ATTEMPTS && response.status === 412; tries++) {
        if (tries > 1) await new Promise(resolve => setTimeout(resolve, 250 + Math.random() * 500))
        const fresh = await refetch()
        if (!fresh) return response
        response = await attempt(fresh)
    }
    return response
}

/**
 * The `refetch` for a form's save: the fresh copy only when nothing the form sends changed in it. A 412 from a write
 * elsewhere on the document, such as a room web's recording moving on in the background (ORB-1087), then saves against
 * the fresh copy, while someone else's change to the same fields still fails the save instead of being overwritten.
 * Every reread is held against the copy the form was opened on, so a later retry overwrites nothing either.
 */
export function refetchUnlessSentFieldsChanged<TDocument>(
    refetch: () => Promise<TDocument | null>,
    current: TDocument,
    sentFields: (document: TDocument) => unknown,
): () => Promise<TDocument | null> {
    return async () => {
        const fresh = await refetch()
        return fresh && JSON.stringify(sentFields(fresh)) === JSON.stringify(sentFields(current)) ? fresh : null
    }
}
